/**
 * JourneyPanel — Active workflow instances widget for subject dashboard.
 *
 * DRR-321: Extracted from SubjectDashboardPage for widget registry.
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Card,
  Box,
  Skeleton,
  Alert,
  ScrollArea,
  Divider,
} from '@mantine/core';
import {
  IconRoute,
  IconPlayerPlay,
  IconCheck,
} from '@tabler/icons-react';

import {
  useGetInstancesBySubjectQuery,
  GqlInstanceStatus,
} from '@/generated/graphql';
import type { WidgetProps } from '@ferp/core';

const STATUS_COLOR: Record<string, string> = {
  [GqlInstanceStatus.RUNNING]: 'green',
  [GqlInstanceStatus.COMPLETED]: 'blue',
  [GqlInstanceStatus.PAUSED]: 'yellow',
  [GqlInstanceStatus.FAILED]: 'red',
  [GqlInstanceStatus.CANCELLED]: 'gray',
};

export function JourneyPanel({ subjectActorId, onJourneyClick }: WidgetProps) {
  const { data, loading } = useGetInstancesBySubjectQuery({
    variables: { subjectActorId },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const instances = data?.instanceApiGetInstancesBySubject ?? [];

  const { activeInstances, completedInstances } = useMemo(() => {
    const active = instances.filter(
      (i) => i.status === GqlInstanceStatus.RUNNING || i.status === GqlInstanceStatus.PAUSED,
    );
    const completed = instances.filter(
      (i) => i.status === GqlInstanceStatus.COMPLETED,
    );
    return { activeInstances: active, completedInstances: completed };
  }, [instances]);

  if (loading && instances.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  if (instances.length === 0) {
    return (
      <Alert icon={<IconRoute size={16} />} color="blue" variant="light">
        No active journeys for this subject.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Text fw={600} size="sm">Active Journeys</Text>
        <Badge size="sm" color="green">{activeInstances.length}</Badge>
      </Group>

      <ScrollArea.Autosize mah={300}>
        <Stack gap="xs">
          {activeInstances.map((instance) => (
            <Card
              key={instance.id}
              withBorder
              padding="xs"
              radius="sm"
              style={{ cursor: onJourneyClick ? 'pointer' : undefined }}
              onClick={() => onJourneyClick?.(instance.id)}
            >
              <Group justify="space-between" wrap="nowrap">
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={500} size="sm" lineClamp={1}>
                    {instance.workflowName ?? instance.instanceName ?? 'Journey'}
                  </Text>
                  <Group gap="xs" mt={2}>
                    <Badge
                      size="xs"
                      color={STATUS_COLOR[instance.status ?? ''] ?? 'gray'}
                      leftSection={
                        instance.status === GqlInstanceStatus.RUNNING ? (
                          <IconPlayerPlay size={8} />
                        ) : instance.status === GqlInstanceStatus.COMPLETED ? (
                          <IconCheck size={8} />
                        ) : null
                      }
                    >
                      {instance.status?.replace(/_/g, ' ')}
                    </Badge>
                    {instance.currentState && (
                      <Badge size="xs" variant="outline" color="gray">
                        {instance.currentState.replace(/_/g, ' ')}
                      </Badge>
                    )}
                  </Group>
                </Box>
              </Group>
            </Card>
          ))}

          {completedInstances.length > 0 && (
            <>
              <Divider my="xs" label="Completed" labelPosition="center" />
              {completedInstances.slice(0, 3).map((instance) => (
                <Card
                  key={instance.id}
                  withBorder
                  padding="xs"
                  radius="sm"
                  style={{ opacity: 0.7, cursor: onJourneyClick ? 'pointer' : undefined }}
                  onClick={() => onJourneyClick?.(instance.id)}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={500} size="sm" lineClamp={1}>
                        {instance.workflowName ?? instance.instanceName ?? 'Journey'}
                      </Text>
                      <Badge size="xs" color="blue" leftSection={<IconCheck size={8} />}>
                        Completed
                      </Badge>
                    </Box>
                  </Group>
                </Card>
              ))}
              {completedInstances.length > 3 && (
                <Text size="xs" c="dimmed" ta="center">
                  +{completedInstances.length - 3} more completed
                </Text>
              )}
            </>
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
}
